import Big from "big.js"
import { TFunction } from "i18next"

import {
  ALL_BUT_DIGITS_REGEX,
  isEmptyOrBlank,
  WHITESPACES_REGEX,
} from "@axtesys/react-tools"

import {
  DetailedReceiptFragment,
  ReceiptContactFragment,
  ReceiptFragment,
  ReceiptItemFragment,
  TaxInfoInput,
  TipTransferFragment,
  ValueTransferFragment,
} from "../../api/graphql/types"
import { toBig } from "../../lib/Big"
import { eurosToBig, formatMoney } from "../../lib/Money"
import {
  DisplayReceipt,
  DisplayTipTransfer,
  DisplayValueTransfer,
  GPtomReceiptData,
  HobexReceiptData,
  ReceiptItem,
  TotalTaxInfo,
} from "../../types"
import { t_General } from "../Translation/lib"

// Internal types

type GeneralTFunction = TFunction<"general">
type TransferTranslationFunctions = {
  tGeneral: GeneralTFunction
  tScreen: TFunction<"screens", "ReceiptDetailsScreen">
}

// Calculation function

export function calculateRoundingDifference(totalTaxInfo: TotalTaxInfo) {
  return totalTaxInfo.gross.minus(totalTaxInfo.correctedGross)
}

// Formatting functions

export function padCashRegisterPart(number: string) {
  return number.padStart(2, "0")
}

export function padReceiptPart(number: string) {
  return number.padStart(6, "0")
}

// Requirements for the Hobex payment section can be found via
// https://www.hobex.at/technet/ecr/api/java/doc/overview-summary.html
// under `Receipt requirements`.
export function formatHobexPaymentSection(
  tGeneral: GeneralTFunction,
  hobexReceiptData: HobexReceiptData,
) {
  return {
    currencyAmount: formatCardPaymentAmount(
      Big(hobexReceiptData.amount ?? 0),
      hobexReceiptData.currency,
    ),
    receiptNumber: hobexReceiptData.receiptNo
      ? padReceiptPart(hobexReceiptData.receiptNo)
      : tGeneral("unknown"),
    terminalId: hobexReceiptData.terminalID ?? tGeneral("unknown"),
    cardIssuer: hobexReceiptData.brand ?? tGeneral("unknown"),
    cardNumber: formatCardNumber(hobexReceiptData.pan) ?? tGeneral("unknown"),
    transactionType: hobexReceiptData.transactionType ?? tGeneral("unknown"),
    responseCode: hobexReceiptData.responseCode ?? tGeneral("unknown"),
  }
}

// Requirements for the GP tom payment section can be found via
// https://www.gptom.com/en/docs/api/uvod/vizual-uctenky/
// under `Payment by card`.
export function formatGPtomPaymentSection(
  tGeneral: GeneralTFunction,
  gpTomReceiptData: GPtomReceiptData,
) {
  return {
    currencyAmount: formatCardPaymentAmount(
      Big(
        gpTomReceiptData.amount
          ? gpTomReceiptData.amount + (gpTomReceiptData.tipAmount ?? 0)
          : 0,
      ),
      gpTomReceiptData.currencyCode,
    ),
    batchNumber: gpTomReceiptData.batchNumber
      ? padReceiptPart(gpTomReceiptData.batchNumber)
      : tGeneral("unknown"),
    receiptNumber: gpTomReceiptData.receiptNumber
      ? padReceiptPart(gpTomReceiptData.receiptNumber)
      : tGeneral("unknown"),
    terminalId: gpTomReceiptData.terminalId ?? tGeneral("unknown"),
    europayAppId: gpTomReceiptData.europayAppId ?? tGeneral("unknown"),
    cardIssuer: gpTomReceiptData.cardIssuer ?? tGeneral("unknown"),
    cardDataEntry: gpTomReceiptData.cardDataEntry,
    cardNumber:
      formatCardNumber(gpTomReceiptData.cardNumber) ?? tGeneral("unknown"),
    transactionType: gpTomReceiptData.transactionType ?? tGeneral("unknown"),
    authorizationCode:
      gpTomReceiptData.authorizationCode?.trim() ?? tGeneral("unknown"),
    sequenceNumber: gpTomReceiptData.sequenceNumber ?? tGeneral("unknown"),
  }
}

function formatCardPaymentAmount(amount: Big, currencyCode?: string) {
  const money = formatMoney(amount, { exact: true, noSymbol: true })

  return !isEmptyOrBlank(currencyCode) ? `${currencyCode} ${money}` : money
}

function formatCardNumber(cardNumber?: string) {
  return cardNumber
    ?.replace(WHITESPACES_REGEX, "")
    ?.replace(ALL_BUT_DIGITS_REGEX, "*")
}

export function formatTransfers(
  { payments, tips, payouts }: DisplayReceipt,
  { tScreen, tGeneral }: TransferTranslationFunctions,
) {
  const paymentRows = payments.map(payment => ({
    name: `${tScreen("given")} ${payment.methodName}`,
    amount: payment.amount,
  }))

  const tipsRows = tips
    ? tips.map(tip => ({
        name: `${tGeneral("tip")} ${tip.originateFrom.methodName}`,
        amount: tip.amount,
      }))
    : []

  const payoutRows = payouts
    ? payouts.map(payout => ({
        name: `${tScreen("moneyAmountReturned")} ${payout.methodName}`,
        amount: payout.amount.times(-1),
      }))
    : []

  return [...paymentRows, ...tipsRows, ...payoutRows]
}

// GraphQL to frontend transformation functions

export function transformFromGraphQLToDisplayReceipt(
  receipt: ReceiptFragment | DetailedReceiptFragment,
): DisplayReceipt {
  return {
    receiptId: receipt.id,
    company: receipt.initialCompany,

    createdAt: receipt.createdAt,
    receiptNumber: receipt.receiptNumber,
    receivedAs: receipt.receivedAs,

    items: receipt.items.map(transformFromGraphQLToReceiptItem),

    payments: receipt.payments.map(transformFromGraphQLToDisplayValueTransfer),
    taxInfos: receipt.taxInfosBps.map(taxInfo => ({
      ...transformFromGraphQLToTaxInfo(taxInfo),
      taxRate: taxInfo.taxRate,
    })),
    totalTaxInfo: {
      ...transformFromGraphQLToTaxInfo(receipt.totalTaxInfo),
      correctedGross: eurosToBig(
        receipt.totalTaxInfo.correctedGross ?? receipt.totalTaxInfo.gross,
      ),
    },

    signatureDeviceFailed: receipt.signatureDeviceFailed ?? true,

    cashRegister: receipt.initialCashRegister,
    operator: receipt.initialOperator,

    receiptType: receipt.receiptType,

    ...transformFromGraphQLToContact(receipt),

    tips: receipt.tips.map(transformFromGraphQLToDisplayTipTransfer),
    payouts: receipt.payouts.map(transformFromGraphQLToDisplayValueTransfer),

    hobexReceiptData: receipt.hobexReceiptData,
    gpTomReceiptData: receipt.gpTomReceiptData,

    qrCodeRepresentation: receipt.qrCodeRepresentation,
    signatureUrlHash: receipt.signatureUrlHash,

    refundsReceiptNumber: receipt.refunds?.receiptNumber,
    refundedByReceiptNumber: receipt.refundedBy?.receiptNumber,
    cancelledByReceiptNumber: receipt.cancelledBy?.receiptNumber,
  }
}

export function transformFromGraphQLToSharedReceiptItem(
  item: ReceiptItemFragment,
) {
  return {
    invoiceItemId: item.id,
    amount: toBig(item.amount)!,
    discountNames: item.discountNames.map(
      name => name ?? t_General().tGeneral("discount"),
    ),
    isNegativeAmount: item.amount < 0,
    taxInfo: {
      ...transformFromGraphQLToTaxInfo(item.taxInfo),
      taxRate: item.initialArticleBps.taxRate,
    },
    additionalText: item.additionalText,
  }
}

function transformFromGraphQLToReceiptItem(
  item: ReceiptItemFragment,
): ReceiptItem {
  return {
    ...transformFromGraphQLToSharedReceiptItem(item),
    article: {
      name: item.initialArticleBps.name,
      taxRate: item.initialArticleBps.taxRate,
      price: eurosToBig(item.initialArticleBps.price),
    },
  }
}

function transformFromGraphQLToDisplayValueTransfer(
  transfer: ValueTransferFragment,
): DisplayValueTransfer {
  return {
    paymentMethodId: transfer.paymentMethodId,
    methodName: transfer.paymentMethodName,
    amount: eurosToBig(transfer.moneyAmount),
  }
}

function transformFromGraphQLToDisplayTipTransfer(
  transfer: TipTransferFragment,
): DisplayTipTransfer {
  return {
    amount: eurosToBig(transfer.moneyAmount),
    originateFrom: { methodName: transfer.originateFrom.paymentMethodName },
  }
}

function transformFromGraphQLToTaxInfo(taxInfo: Omit<TaxInfoInput, "taxRate">) {
  return {
    net: eurosToBig(taxInfo.net),
    tax: eurosToBig(taxInfo.tax),
    gross: eurosToBig(taxInfo.gross),
    discount: eurosToBig(taxInfo.discount),
    undiscountedGross: eurosToBig(taxInfo.undiscountedGross),
  }
}

function transformFromGraphQLToContact(contact: ReceiptContactFragment) {
  return {
    billingAddress: contact.initialBillingAddress,
    department: contact.initialDepartment,
    shippingAddress: contact.initialShippingAddress,
    receiptText: contact.receiptText,
  }
}
